/**
 *  Date    : 2019/9/4
 *  Author  : CastileMan
 *  Declare : 常用hooks
 */
import dayjs from '@/utils/dayjs';
import {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect
} from 'react';
import { useLocation } from 'react-router-dom';

import Utils from '@/utils/utils';
import { NOOP } from '@/constants/common';
/**
 *下拉加载hook，用于更新或重置分页参数
 *
 * @export
 * @param disabled [optional]: true表示禁用滚动加载
 * @param debounce [optional]: 防抖delay，防止连续触发
 * @returns scrollFetcher
 */
export function useScrollFetch(options = {}) {
  const { disabled = false, debounce = 500 } = options;
  const [currentPage, _setCurrentPage] = useState(0);

  // add debounce
  const setCurrentPage = useCallback(Utils.debounce((...args) => {
    _setCurrentPage(...args);
  }, debounce), []);

  return useMemo(() => {
    return {
      current: currentPage,
      update() {
        if(disabled) return;
        setCurrentPage(index => index + 1);
      },
      reset() {
        setCurrentPage(0);
      }
    };
  }, [currentPage, disabled, setCurrentPage]);
}

/**
 * 与useEffect相似，但首次不执行callback
 * @param {Function} callback
 * @param {Array} args
 */
export function useLoadEffect(callback = NOOP, args = []) {
  const firstLoadRef = useRef(true);
  useEffect(() => {
    if(firstLoadRef.current) {
      firstLoadRef.current = false;
    } else {
      callback();
    }

  }, args);
}

let urlQuery = {};
/**
 * @returns {Object} location中增加query对象
 */
export function useUrlInfo() {
  const location = useLocation();
  let search = location.search;
  if(search.startsWith('?')) search = search.slice(1);
  const query = Utils.params2json(search);
  if(!Utils.isObjectValueEqual(query, urlQuery)) {
    // Object.assign(urlQuery, Utils.params2json(search));
    urlQuery = query;
  }
  return {
    ...location,
    query: urlQuery
  };
}

/**
 * @description 自滚动效果
 * @param {*} target 滚动dom节点
 * @param {*} scrollHeight 满足滚动条件的高度值，默认为clientHeight的1.3倍
 * @returns [start, stop] 开始滚动，停止滚动
 */
export function useAutoScroll(target, scrollHeight) {
  const timerRef = useRef();
  const startTimerRef = useRef();
  const [scroll, setScroll] = useState(true);

  useEffect(() => {
    return () => {
      if(timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if(target) {
      target.addEventListener('touchstart', stop);
      target.addEventListener('touchmove', stop);
      target.addEventListener('touchend', () => {
        startTimerRef.current = setTimeout(start, 2500);
      });
    } else {
      stop();
    }
  }, [target]);

  useEffect(() => {
    if(target && scroll) {
      if(target.scrollHeight < (scrollHeight || target.clientHeight * 1.3)) {
        stop();
      } else {
        timerRef.current = setInterval(() => {
          target.scrollTop = target.scrollTop + 1;
          if(target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
            target.scrollTop = 0;
          }
        }, [20]);
      }
    }
  }, [target, scroll]);

  function start() {
    setScroll(true);
  }

  function stop() {
    if(startTimerRef.current) {
      clearTimeout(startTimerRef.current);
    }
    if(timerRef.current) {
      clearInterval(timerRef.current);
    }
    setScroll(false);
  }

  return [start, stop];
}

// 获取剩余天，小时，分，秒
export const useTimeLeft = (endTime) => {
  const timeLeft = useMemo(() => {
    const endDate = dayjs(endTime);
    const now = dayjs();
    const durations = dayjs.duration(endDate.diff(now));
    return {
      days: endDate.diff(now, 'days'),
      hours: durations.hours(),
      minutes: durations.minutes(),
      seconds: durations.seconds()
    };
  }, [endTime]);
  return timeLeft;
};

export const useSyncCallback = callback => {
  const [proxyState, setProxyState] = useState({ current: false });
  const [params, setParams] = useState([]);
  const Func = useCallback((...args) => {
    setParams(args);
    setProxyState({ current: true });
  }, [proxyState]);

  useEffect(() => {
    if(proxyState.current === true) setProxyState({ current: false });
  }, [proxyState]);

  useEffect(() => {
    proxyState.current && callback(...params);
  });
  return Func;
};
